import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { createAPIClient } from '../../api';
import { cellClass, DateField, EmptyRow, FilterGrid, Pager, RangeError, ReportCard, ReportStatus, ReportTable, SummaryTiles, useReportData, useReportParams } from './ReportParts';
import { dateParam, dateRangeError, isZeroAmount, pageParam, REPORT_PAGE_SIZE, reportDate, reportMoney, statementLinePath } from './reportUi';
import { statementPrintPath } from '../print/printUi';
import { secondaryButtonClass } from '../../components/formStyles';
import { EmailDocumentButton } from '../../components/EmailDocumentButton';
import * as types from '../../api/types';

const COLUMNS = [
  { label: 'Date' }, { label: 'Type' }, { label: 'Reference' }, { label: 'Description' },
  { label: 'Charge', numeric: true }, { label: 'Credit', numeric: true }, { label: 'Balance', numeric: true },
];

export function CustomerStatementPage() {
  const api = useMemo(() => createAPIClient(), []);
  const { customerId = '' } = useParams<{ customerId: string }>();
  const { params, change } = useReportParams();
  const startDate = dateParam(params.get('startDate'));
  const endDate = dateParam(params.get('endDate'));
  const page = pageParam(params.get('page'));
  const rangeError = dateRangeError(startDate, endDate);
  const query = { startDate, endDate, page, limit: REPORT_PAGE_SIZE };
  const state = useReportData(
    rangeError || !customerId ? null : JSON.stringify({ customerId, ...query }),
    () => api.getCustomerStatement(customerId, query),
    'Could not load the statement',
  );
  const customerName = state.data?.customerName;
  // The recipient for "Email" (the statement report does not include contact details)
  const [customer, setCustomer] = useState<types.Customer | null>(null);
  useEffect(() => {
    let active = true;
    // Best effort: without it the Email button is just not shown
    if (customerId) Promise.resolve().then(() => api.getCustomer(customerId)).then((c) => { if (active) setCustomer(c); }).catch(() => undefined);
    return () => { active = false; };
  }, [api, customerId]);

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <div className="flex flex-wrap gap-4 text-sm font-medium text-gray-500">
        <Link to={`/customers/${customerId}`} className="hover:text-gray-900">&larr; {customerName || 'Customer'}</Link>
        <Link to="/reports/ar-aging" className="hover:text-gray-900">AR aging</Link>
      </div>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-gray-900 min-w-0 break-words">Statement{customerName ? `: ${customerName}` : ''}</h1>
        {/* Prints every line for the selected range, not just this page. */}
        <div className="flex flex-wrap items-start gap-2">
          {!rangeError && customerId && <Link to={statementPrintPath(customerId, { startDate, endDate })} target="_blank" rel="noopener noreferrer" className={secondaryButtonClass}>Print</Link>}
          {customer && <EmailDocumentButton kind="statement" id={customerId} recipient={customer.emailInvalid ? null : customer.email} documentLabel={`the statement for ${customer.name}`} disabledReason={customer.emailInvalid ? 'The email address bounced; update it first.' : null} />}
        </div>
      </div>
      <p className="mt-1 mb-6 text-sm text-gray-600 max-w-3xl">
        Invoices (charges, including tax) and payments (credits) with a running balance. Activity before the start date rolls into the opening balance. DRAFT and VOID invoices and voided payments are excluded.
      </p>
      <div className="space-y-6">
        <FilterGrid>
          <DateField id="statement-start" label="Start date" value={startDate} onChange={(value) => change({ startDate: value })} />
          <DateField id="statement-end" label="End date" value={endDate} onChange={(value) => change({ endDate: value })} />
        </FilterGrid>
        <RangeError message={rangeError} />
        <ReportStatus state={state} label="statement">
          {(report) => (
            <>
              <SummaryTiles tiles={[
                { label: 'Customer', value: <Link to={`/customers/${report.customerId}`} className="text-blue-700 hover:underline">{report.customerName}</Link> },
                { label: 'Period', value: `${report.startDate ? reportDate(report.startDate) : 'Beginning'} – ${report.endDate ? reportDate(report.endDate) : 'Latest'}` },
                { label: 'Opening balance', value: reportMoney(report.openingBalance) },
                { label: 'Closing balance', value: reportMoney(report.closingBalance) },
              ]} />
              <ReportCard title="Activity">
                <ReportTable columns={COLUMNS} caption="Statement lines">
                  {report.lines.length === 0 ? <EmptyRow colSpan={COLUMNS.length} message="No invoices or payments in this period." /> : report.lines.map((line) => (
                    <tr key={`${line.type}-${line.referenceId}`}>
                      <td data-label="Date" className={cellClass()}>{reportDate(line.date)}</td>
                      <td data-label="Type" className={cellClass()}>{line.type === 'PAYMENT' ? 'Payment' : 'Invoice'}</td>
                      <td data-label="Reference" data-primary className={cellClass()}><Link to={statementLinePath(line)} className="text-blue-700 hover:underline break-words">{line.referenceNumber}</Link></td>
                      <td data-label="Description" className="px-4 py-3 text-left break-words">{line.description}</td>
                      <td data-label="Charge" className={cellClass(true)}>{isZeroAmount(line.charge) ? '' : reportMoney(line.charge)}</td>
                      <td data-label="Credit" className={cellClass(true)}>{isZeroAmount(line.credit) ? '' : reportMoney(line.credit)}</td>
                      <td data-label="Balance" className={cellClass(true)}>{reportMoney(line.runningBalance)}</td>
                    </tr>
                  ))}
                </ReportTable>
                <Pager pagination={report.pagination} noun="line" onPage={(next) => change({ page: next > 1 ? String(next) : '' })} />
              </ReportCard>
            </>
          )}
        </ReportStatus>
      </div>
    </main>
  );
}
